import { FC, useState } from 'react';

import { recordRenewal } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  formatDeliveryChannelSelectLabel,
  NotificationType,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { createGiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.controller';
import { kstToday } from '@shared/utils/kstTime.util';

/** 연장 처리 대상 — 화면마다 쿠폰 타입이 달라 필요한 값만 받는다 */
export interface RenewalRecordTarget {
  /** 연장할 쿠폰 (발송완료 건) — 연장 내역은 이 쿠폰에 그대로 기록된다 */
  couponId: string;
  /** "#3 · ABC123" 식 표기 */
  label?: string;
  itemName?: string;
  /** 원본 발송 때의 도매가·매체 — 도매가 기본값·비교 기준 */
  priceWhole?: number;
  deliveryChannel?: DeliveryChannel;
  /** 교환 신청번호 (recipient.phoneNumber) — 안내 문자 수신번호 기본값 */
  recipientPhone?: string;
}

interface RenewalRecordModalProps {
  target: RenewalRecordTarget;
  /** recorded: 처리가 완료됐는지 — 부모가 목록 갱신 여부를 정한다 */
  onClose: (recorded: boolean) => void;
}

const onlyDigits = (raw: string | undefined): string => (raw ?? '').replace(/\D/g, '');

const formatPhone = (raw: string | undefined): string => {
  const d = onlyDigits(raw);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return raw ?? '';
};

const isValidPhone = (digits: string) => digits.length === 10 || digits.length === 11;

const errorStatus = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const errorMessage = (e: unknown): string | undefined =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message;

/**
 * 연장 안내 문자 기본 문구 — 수령 방법 줄은 매체에 따라 달라진다
 * (수신 페이지 CouponStatusView의 발송 안내와 같은 기준: 매체 notificationType·발신번호).
 */
export const buildRenewalNoticeMessage = (channel: DeliveryChannel): string => {
  const config = DELIVERY_CHANNEL_CONFIG[channel];
  const receiveLine =
    config.notificationType === NotificationType.AlimTalk
      ? '카카오톡 알림톡으로 발송되며, 수신 환경에 따라 문자로 도착할 수 있습니다.'
      : '문자로 발송되며, 수신 환경에 따라 카카오톡 알림톡으로 도착할 수 있습니다.';
  return [
    '[답례품 연장 안내]',
    '안녕하세요. 문의하신 답례품을 연장 처리하여 기프티콘을 새로 발송해 드렸습니다.',
    '',
    '[받으시는 방법]',
    receiveLine,
    ...(config.senderNumber ? [`발신번호: ${config.senderNumber}`] : []),
    '',
    '[꼭 확인해 주세요]',
    '- 새로 받으신 기프티콘의 유효기간을 확인하시고, 기간 안에 꼭 사용해 주세요.',
    '- 이번 연장은 1회에 한해 도와드린 것으로, 유효기간이 지나면 추가 연장이나 재발송이 어렵습니다.',
  ].join('\n');
};

/**
 * 연장 처리 — 고객 연장 신청(링크·인증·서명) 없이, 매체에서 기프티콘을 새로 보내준 뒤
 * 관리자가 매체·도매가만 입력해 끝낸다. 새 쿠폰은 만들지 않고 이 쿠폰에 연장 내역이
 * 붙는다(환불과 같은 구조). 입력한 도매가가 상조 정산의 연장 차감(−)과
 * 충전금 원장 차감으로 잡히고, 받는 분께 연장 안내 문자가 나간다(끄면 기록만).
 * 스펙: docs/gift-coupon-renewal-deduct-spec.md
 */
export const RenewalRecordModal: FC<RenewalRecordModalProps> = ({ target, onClose }) => {
  // 기본 매체는 애드콘 — 원본 매체와 무관하게 연장 재구매는 애드콘이 대부분이다
  const [channel, setChannel] = useState<DeliveryChannel>(DeliveryChannel.Addcon);
  const [priceWhole, setPriceWhole] = useState(
    target.priceWhole != null ? String(target.priceWhole) : '',
  );
  const [sentDate, setSentDate] = useState(kstToday());
  const [memo, setMemo] = useState('');

  const [sendNotice, setSendNotice] = useState(true);
  const [phone, setPhone] = useState(formatPhone(target.recipientPhone));
  // 문구를 손대기 전까지는 매체를 바꾸면 수령 방법 줄이 따라 바뀐다
  const [message, setMessage] = useState(buildRenewalNoticeMessage(DeliveryChannel.Addcon));
  const [messageEdited, setMessageEdited] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = kstToday();
  const priceNum = Number(priceWhole);
  const priceValid = priceWhole.trim() !== '' && Number.isFinite(priceNum) && priceNum > 0;
  const dateValid = sentDate !== '' && sentDate <= today;
  const phoneDigits = onlyDigits(phone);
  const noticeValid = !sendNotice || (isValidPhone(phoneDigits) && message.trim() !== '');
  const canSubmit = priceValid && dateValid && noticeValid && !isSubmitting;
  const priceDiffers =
    priceValid && target.priceWhole != null && priceNum !== target.priceWhole;

  const handleChangeChannel = (next: DeliveryChannel) => {
    setChannel(next);
    if (!messageEdited) setMessage(buildRenewalNoticeMessage(next));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await recordRenewal({
        couponId: target.couponId,
        priceWhole: priceNum,
        deliveryChannel: channel,
        // 오늘이면 서버 현재 시각. 지난 날짜는 KST 정오로 고정해 월 경계에서 밀리지 않게 한다
        ...(sentDate !== today && { deliveredAt: `${sentDate}T12:00:00+09:00` }),
        ...(sendNotice && {
          notice: { phoneNumber: phoneDigits, message: message.trim() },
        }),
      });
      if (sendNotice && result?.noticeSent === false) {
        alert(
          `연장은 처리됐지만 안내 문자 발송에 실패했습니다.${
            result.noticeReason ? `\n(${result.noticeReason})` : ''
          }\n받는 분께 따로 안내해 주세요.`,
        );
      }
    } catch (e) {
      setError(
        errorStatus(e) === 404
          ? '서버에 아직 연장 처리 기능이 배포되지 않았습니다.'
          : errorMessage(e) ?? '연장 처리 중 오류가 발생했습니다.',
      );
      setIsSubmitting(false);
      return;
    }

    // 경위는 CS 메모로 남긴다 — 처리 자체는 끝났으므로 메모 실패가 처리를 되돌리지 않는다
    if (memo.trim()) {
      try {
        await createGiftCsMemo({
          giftCoupon: target.couponId,
          body: `[연장] ${memo.trim()}`,
        });
      } catch {
        alert('연장은 처리됐지만 메모 저장에 실패했습니다. CS 메모에 직접 남겨 주세요.');
      }
    }
    onClose(true);
  };

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/40"
        onClick={() => !isSubmitting && onClose(false)}
      />
      <div className="relative flex max-h-[92vh] w-full max-w-md flex-col overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-700">
            연장
          </span>
          <h3 className="text-lg font-bold text-gray-900">연장 처리</h3>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          매체에서 기프티콘을 새로 보낸 뒤 매체·도매가를 입력합니다. 신청·서명 절차 없음.
        </p>

        {(target.label || target.itemName) && (
          <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm">
            {target.label && (
              <span className="font-semibold text-gray-800">{target.label}</span>
            )}
            {target.itemName && (
              <span className="ml-2 text-gray-600">{target.itemName}</span>
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              발송 매체 <span className="text-red-500">*</span>
            </label>
            <select
              value={channel}
              onChange={(e) => handleChangeChannel(e.target.value as DeliveryChannel)}
              className={`mt-1 bg-white ${inputClass}`}
            >
              {DELIVERY_CHANNEL_LIST.map((ch) => (
                <option key={ch} value={ch}>
                  {formatDeliveryChannelSelectLabel(ch)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              연장 도매가 <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <input
                type="number"
                min={0}
                value={priceWhole}
                onChange={(e) => setPriceWhole(e.target.value)}
                placeholder="다시 구매한 금액"
                className={`pr-8 ${inputClass}`}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                원
              </span>
            </div>
          </div>
        </div>
        {target.priceWhole != null && (
          <p className={`mt-1 text-xs ${priceDiffers ? 'text-amber-600' : 'text-gray-400'}`}>
            원래 발송 도매가 {target.priceWhole.toLocaleString()}원
            {target.deliveryChannel &&
              ` · ${DELIVERY_CHANNEL_CONFIG[target.deliveryChannel]?.label ?? ''}`}
            {priceDiffers && ' — 입력한 금액과 다릅니다'}
          </p>
        )}

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">연장 발송일</label>
          <input
            type="date"
            value={sentDate}
            max={today}
            onChange={(e) => setSentDate(e.target.value)}
            className={`mt-1 ${inputClass}`}
          />
          {!dateValid && (
            <p className="mt-1 text-xs text-red-600">오늘 이후 날짜는 입력할 수 없습니다.</p>
          )}
        </div>

        {/* 안내 문자 — 끄면 기록만 남는다(이미 안내까지 끝낸 건) */}
        <div className="mt-4 rounded-lg border border-gray-200 p-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={sendNotice}
              onChange={(e) => setSendNotice(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            연장 안내 문자 발송
          </label>
          {sendNotice ? (
            <div className="mt-3 space-y-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="수신번호"
                className={inputClass}
              />
              {!isValidPhone(phoneDigits) && (
                <p className="text-xs text-red-600">수신번호를 확인해 주세요.</p>
              )}
              <textarea
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setMessageEdited(true);
                }}
                rows={9}
                className={`text-xs leading-relaxed ${inputClass}`}
              />
              {messageEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setMessage(buildRenewalNoticeMessage(channel));
                    setMessageEdited(false);
                  }}
                  className="text-xs text-violet-700 hover:underline"
                >
                  기본 문구로
                </button>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              문자 없이 기록만 남깁니다. 이미 안내까지 끝낸 건에 사용.
            </p>
          )}
        </div>

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            메모{' '}
            <span className="text-xs font-normal text-gray-400">(선택 · CS 메모 저장)</span>
          </label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="연장 사유"
            rows={2}
            className={`mt-1 ${inputClass}`}
          />
        </div>

        <ul className="mt-4 space-y-1 rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-900">
          <li>
            상조 정산 — 연장 차감 <b>−{priceValid ? priceNum.toLocaleString() : '0'}원</b>{' '}
            (원래 발송분 수익금은 유지)
          </li>
          <li>충전금 원장 — 선택한 매체에서 같은 금액 차감</li>
        </ul>

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={() => onClose(false)}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            닫기
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {isSubmitting ? '처리 중...' : sendNotice ? '연장 처리 + 문자 발송' : '연장 처리 (기록만)'}
          </button>
        </div>
      </div>
    </div>
  );
};
